import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { 
  Search as SearchIcon, 
  FileSpreadsheet, 
  Trash2,
  Calendar,
  AlertTriangle,
  Copy,
  ChevronDown,
  RotateCcw,
  TrendingDown,
  HelpCircle,
  CheckCircle2,
  Loader2,
  Inbox,
  Activity,
  Wallet,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { initializeApp } from "firebase/app";
import { 
  getFirestore, 
  collection, 
  onSnapshot, 
  query, 
  where, 
  writeBatch, 
  getDocs 
} from 'firebase/firestore';

// Inisialisasi Firebase
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE,
  messagingSenderId: process.env.REACT_APP_FIREBASE_SENDER,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// Helper tanggal WIB (Asia/Jakarta) format YYYY-MM-DD, konsisten di semua timezone perangkat
const getWibDayString = (date = new Date()) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jakarta', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);

// Tinggi tabel agar 20 transaksi tampil sekaligus tanpa scroll vertikal
// (header 44px + 20 baris x 50px + ruang scrollbar horizontal 12px)
const TABLE_HEADER_HEIGHT = 44;
const TABLE_ROW_HEIGHT = 50;
const VISIBLE_TRX_ROWS = 20;
const TABLE_MAX_HEIGHT = TABLE_HEADER_HEIGHT + TABLE_ROW_HEIGHT * VISIBLE_TRX_ROWS + 12;

// ============================================================
// HELPER MURNI (di luar komponen: tidak dibuat ulang tiap render)
// ============================================================
const formatRupiah = (val) => {
  const num = Number(val);
  if (isNaN(num)) return val ? String(val) : "0";
  return num.toLocaleString('id-ID');
};

const evaluateLaba = (labaVal) => {
  if (labaVal === null || labaVal === undefined) {
    return { isProblem: true, reason: "Laba Kosong / NA" };
  }
  const strVal = String(labaVal).trim().toUpperCase();
  if (strVal === "" || strVal === "NA" || strVal === "N/A" || strVal === "NULL" || strVal === "UNDEFINED") {
    return { isProblem: true, reason: "Laba N/A atau Kosong" };
  }
  const numVal = Number(String(labaVal).replace(/,/g, '').trim());
  if (isNaN(numVal)) {
    return { isProblem: true, reason: `Format Laba Tidak Valid (${strVal})` };
  }
  if (numVal < 0) {
    return { isProblem: true, reason: `Laba Negatif (${formatRupiah(numVal)})` };
  }
  return { isProblem: false, reason: "" };
};

const isProblemSn = (sn) => {
  const u = String(sn || '').trim().toUpperCase();
  return u === 'N/A' || u === 'NA';
};

const statTone = {
  indigo: 'bg-indigo-50 text-indigo-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  rose: 'bg-rose-50 text-rose-600',
  slate: 'bg-slate-100 text-slate-600'
};

// ============================================================
// KOMPONEN KECIL (di luar komponen utama + memo)
// Sebelumnya dibuat ulang di setiap render sehingga seluruh sel tabel
// di-mount ulang tiap kali mengetik / mengubah filter.
// ============================================================
const StatCard = memo(({ icon: Icon, label, value, tone = 'slate', valueClass = 'text-slate-900' }) => (
  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-3 shadow-sm">
    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${statTone[tone]}`}>
      <Icon size={17} />
    </div>
    <div className="min-w-0">
      <p className="sl-clip text-[11px] font-medium text-slate-500">{label}</p>
      <p className={`sl-clip text-[15px] font-bold leading-tight tabular-nums ${valueClass}`}>{value}</p>
    </div>
  </div>
));

const StatusBadge = memo(({ status }) => {
  const s = String(status || '').toLowerCase();
  let cls = 'bg-slate-100 text-slate-600 ring-slate-200';
  if (s === 'sukses') cls = 'bg-emerald-50 text-emerald-700 ring-emerald-200';
  else if (s === 'gagal') cls = 'bg-rose-50 text-rose-700 ring-rose-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${cls}`}>
      {status || '-'}
    </span>
  );
});

const DateCell = memo(({ value }) => {
  const [d, ...rest] = String(value || '-').split(' ');
  return (
    <div className="flex flex-col justify-center leading-[1.25] tabular-nums">
      <span className="text-[12px] text-slate-800">{d}</span>
      <span className="text-[11px] text-slate-400">{rest.join(' ')}</span>
    </div>
  );
});

const HeaderCell = memo(({ title, colKey, width, align = 'left', onResizeStart }) => (
  <th
    style={{ width, textAlign: align }}
    className="sl-th relative select-none"
  >
    <div className={`flex h-full w-full items-center px-2.5 ${align === 'right' ? 'justify-end' : 'justify-start'}`}>
      <span className="sl-clip">{title}</span>
    </div>
    <div
      onMouseDown={(e) => onResizeStart(e, colKey, width)}
      onTouchStart={(e) => onResizeStart(e, colKey, width)}
      className="sl-resizer"
    />
  </th>
));

// Satu baris transaksi. Lebar kolom diambil dari <th> (tableLayout: fixed),
// jadi baris tidak perlu dirender ulang saat kolom digeser.
const TrxRow = memo(({ item, idx }) => {
  const isGagal = String(item.status).toLowerCase() === 'gagal';
  const isNA = isProblemSn(item.sn);
  const isLabaMinus = Number(item.laba) < 0;

  let rowClass = idx % 2 === 1 ? 'sl-row-alt' : '';
  if (isGagal) rowClass = 'sl-row-fail';
  else if (isNA) rowClass = 'sl-row-na';
  else if (isLabaMinus) rowClass = 'sl-row-minus';

  return (
    <tr className={`sl-row ${rowClass}`}>
      <td className="sl-td sl-td-idx">{idx + 1}</td>
      <td className="sl-td" title={item.tglEntri}><DateCell value={item.tglEntri} /></td>
      <td className="sl-td" title={item.tglStatus}><DateCell value={item.tglStatus} /></td>
      <td className="sl-td" title={item.produk}>{item.produk}</td>
      <td className="sl-td tabular-nums" title={item.tujuan}>{item.tujuan}</td>
      <td className={`sl-td font-mono text-[11.5px] ${isNA ? 'font-bold text-amber-700' : ''}`} title={item.sn}>{item.sn}</td>
      <td className="sl-td"><StatusBadge status={item.status} /></td>
      <td className="sl-td text-right tabular-nums">{formatRupiah(item.hargaJual)}</td>
      <td className="sl-td text-right tabular-nums">{formatRupiah(item.hargaBeli)}</td>
      <td className="sl-td text-right tabular-nums">
        {isLabaMinus
          ? <span className="rounded-md bg-rose-100 px-1.5 py-0.5 font-semibold text-rose-700">{formatRupiah(item.laba)}</span>
          : <span className="text-emerald-700">{formatRupiah(item.laba)}</span>}
      </td>
      <td className="sl-td" title={item.supplier}>{item.supplier}</td>
      <td className="sl-td text-slate-600" title={item.jawabanProvider}>{item.jawabanProvider}</td>
    </tr>
  );
});

// Virtualisasi baris: hanya baris di sekitar area yang terlihat yang dirender,
// sisanya diganti spacer setinggi baris asli. Tampilan, nomor urut, warna
// selang-seling, dan scroll tetap sama persis walau datanya ribuan.
const VIRTUAL_OVERSCAN = 10;
const VIRTUAL_STEP = 5;
const VIRTUAL_WINDOW = VISIBLE_TRX_ROWS + 2 + VIRTUAL_OVERSCAN * 2;

const VirtualRows = ({ rows, scrollRef }) => {
  const [firstVisible, setFirstVisible] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    let raf = null;

    const compute = () => {
      raf = null;
      const rawFirst = Math.max(0, Math.floor((el.scrollTop - TABLE_HEADER_HEIGHT) / TABLE_ROW_HEIGHT));
      const first = Math.floor(rawFirst / VIRTUAL_STEP) * VIRTUAL_STEP;
      setFirstVisible(prev => (prev === first ? prev : first));
    };
    const onScroll = () => {
      if (raf === null) raf = requestAnimationFrame(compute);
    };

    compute();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [scrollRef]);

  const len = rows.length;
  const start = Math.min(Math.max(0, firstVisible - VIRTUAL_OVERSCAN), Math.max(0, len - VIRTUAL_WINDOW));
  const end = Math.min(len, start + VIRTUAL_WINDOW);
  const topPad = start * TABLE_ROW_HEIGHT;
  const bottomPad = (len - end) * TABLE_ROW_HEIGHT;

  return (
    <>
      {topPad > 0 && (
        <tr aria-hidden="true" style={{ height: topPad }}>
          <td colSpan={12} style={{ height: topPad, padding: 0, border: 0 }} />
        </tr>
      )}
      {rows.slice(start, end).map((item, i) => (
        <TrxRow key={item.id || start + i} item={item} idx={start + i} />
      ))}
      {bottomPad > 0 && (
        <tr aria-hidden="true" style={{ height: bottomPad }}>
          <td colSpan={12} style={{ height: bottomPad, padding: 0, border: 0 }} />
        </tr>
      )}
    </>
  );
};

const LogSpeedPanel = () => {
  const [dbRaw, setDbRaw] = useState([]); // data mentah dari Firestore (diolah lewat useMemo)
  const [manualData, setManualData] = useState([]);
  const [isManualUpload, setIsManualUpload] = useState(false);
  const [fileName, setFileName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Input pencarian di-debounce supaya tabel tidak difilter di setiap ketikan
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState('');
  const [isAutoFit, setIsAutoFit] = useState(false);
  const fileInputRef = useRef(null);

  const [useCustomUploadDate, setUseCustomUploadDate] = useState(false);
  const [customUploadDate, setCustomUploadDate] = useState('');
  const [filterNaOnly, setFilterNaOnly] = useState(false);

  // State Performa: Loading & Error
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // Kunci hari berjalan (WIB); berganti otomatis saat lewat tengah malam
  const [wibDayKey, setWibDayKey] = useState(() => getWibDayString());
  useEffect(() => {
    const dayTimer = setInterval(() => {
      const nowKey = getWibDayString();
      setWibDayKey(prev => (prev === nowKey ? prev : nowKey));
    }, 30000);
    return () => clearInterval(dayTimer);
  }, []);

  // Default Lebar Kolom
  const defaultWidths = useMemo(() => ({
    tglEntri: 140,
    tglStatus: 140,
    produk: 90,
    tujuan: 130,
    sn: 200,
    status: 90,
    hargaJual: 105,
    hargaBeli: 105,
    laba: 95,
    supplier: 115,
    jawabanProvider: 320
  }), []);

  const [columnWidths, setColumnWidths] = useState(defaultWidths);
  const resizingColumn = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  // Helper String Tanggal YYYY-MM-DD
  const getTodayString = useCallback(() => {
    return getWibDayString();
  }, []);

  const parseStatusFromRow = useCallback((item) => {
    let rawStatus = item['S'] || item['s'] || item.status || item.Status || item.STATUS || item.status_provider || item.statusProvider || item['Keterangan Status'] || "";

    if (!rawStatus) {
      const foundKey = Object.keys(item).find(k => k.toLowerCase() === 'status' || k.toLowerCase() === 'sts');
      if (foundKey) rawStatus = item[foundKey];
    }

    const kolomS = String(rawStatus).trim().toUpperCase();
    const jawabanProvider = String(item['N'] || item.jawabanProvider || item['Jawaban Provider'] || item.message || item.Message || "").trim().toUpperCase();

    const failKeywords = [
      "GAGAL", "REJECT", "CANCEL", "FAIL", "BATAL", "ERROR", 
      "SISA SALDO TIDAK CUKUP", "SALDO TIDAK CUKUP", "PIN SALAH", 
      "EXPIRED", "REFUND", "RJT", "DITOLAK", "TIBA-TIBA GAGAL", "INVALID"
    ];

    if (failKeywords.some(kw => kolomS.includes(kw)) || failKeywords.some(kw => jawabanProvider.includes(kw))) {
      return "Gagal";
    }

    const successKeywords = ["SUKSES", "SUCCESS", "OK", "BERHASIL"];
    if (successKeywords.some(kw => kolomS.includes(kw)) || successKeywords.some(kw => jawabanProvider.includes(kw))) {
      return "Sukses";
    }

    if (kolomS) {
      const str = String(kolomS).trim();
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    return "Sukses";
  }, []);

  const parseSupplierFromRow = useCallback((item) => {
    let raw = item.supplier || item.Supplier || item.SUPPLIER || item.vendor || item.Vendor || item['Nama Supplier'] || item['Provider'] || "";
    if (!raw && item['H']) raw = item['H'];

    let str = String(raw).trim();
    if (str && str.toUpperCase() !== "UNKNOWN" && str.toUpperCase() !== "") {
      return str.toUpperCase();
    }

    const combinedText = `${item['N'] || ''} ${item['D'] || ''} ${item['F'] || ''} ${item['S'] || ''} ${item.jawabanProvider || ''}`.toUpperCase();
    
    if (combinedText.includes("DIGIFLAZZ")) return "DIGIFLAZZ";
    if (combinedText.includes("DAS")) return "DAS";
    if (combinedText.includes("AMC")) return "AMC";
    if (combinedText.includes("HAO")) return "HAO";
    if (combinedText.includes("NEWBIEZ")) return "NEWBIEZ";
    if (combinedText.includes("OASIS")) return "OASIS";
    if (combinedText.includes("ANTUM")) return "ANTUM";

    return str ? str.toUpperCase() : "UNKNOWN";
  }, []);

  const parseExcelDate = useCallback((val) => {
    if (!val) return new Date();
    if (val instanceof Date && !isNaN(val)) return val;
    if (val && typeof val.toDate === 'function') return val.toDate();

    if (typeof val === 'number') {
      const parsedDate = XLSX.SSF.parse_date_code(val);
      if (parsedDate) {
        return new Date(
          parsedDate.y, 
          parsedDate.m - 1, 
          parsedDate.d, 
          parsedDate.H || 0, 
          parsedDate.M || 0, 
          parsedDate.S || 0
        );
      }
    }

    const str = String(val).trim();
    if (!str) return new Date();

    const regexFull = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})(?:\s+(\d{1,2})[:\.](\d{1,2})(?:[:\.](\d{1,2}))?)?$/;
    const match = str.match(regexFull);

    if (match) {
      let day = parseInt(match[1], 10);
      let month = parseInt(match[2], 10) - 1;
      let year = parseInt(match[3], 10);
      if (year < 100) year += 2000;

      let hour = match[4] ? parseInt(match[4], 10) : 0;
      let min = match[5] ? parseInt(match[5], 10) : 0;
      let sec = match[6] ? parseInt(match[6], 10) : 0;

      return new Date(year, month, day, hour, min, sec);
    }

    const stdDate = new Date(str);
    return isNaN(stdDate.getTime()) ? new Date() : stdDate;
  }, []);

  const formatOtomaxDate = useCallback((dateObj, manualTime = null) => {
    if (!(dateObj instanceof Date) || isNaN(dateObj)) return "-";
    const pad = (num) => num.toString().padStart(2, '0');
    const d = pad(dateObj.getDate());
    const m = pad(dateObj.getMonth() + 1);
    const y = dateObj.getFullYear().toString().slice(-2);
    
    let timeStr = "";
    if (manualTime) {
      timeStr = manualTime.replace(/:/g, '.');
    } else {
      const h = pad(dateObj.getHours());
      const min = pad(dateObj.getMinutes());
      const s = pad(dateObj.getSeconds());
      timeStr = `${h}.${min}.${s}`;
    }
    
    return `${d}/${m}/${y} ${timeStr}`;
  }, []);

  const parseOasisTglStatus = useCallback((jawabanProvider, entriDate) => {
    if (!jawabanProvider) return null;

    const oasisRegex = /\((\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})(\d{1,2}[:\.]\d{2}[:\.]\d{2})\)/i;
    const match = jawabanProvider.match(oasisRegex);

    if (match) {
      const yearFull = match[3];
      const year2Digit = yearFull.slice(-2);
      const rawTime = match[4];

      const timeParts = rawTime.split(/[:\.]/);
      if (timeParts.length === 3) {
        let hourStr = timeParts[0];
        let minStr = timeParts[1];
        let secStr = timeParts[2];

        if (hourStr.length > 2 || (parseInt(hourStr, 10) > 23 && hourStr.length === 2)) {
          hourStr = hourStr.slice(-1);
        }

        const formattedHour = hourStr.padStart(2, '0');
        const formattedMin = minStr.padStart(2, '0');
        const formattedSec = secStr.padStart(2, '0');

        const pad = (num) => num.toString().padStart(2, '0');
        const day = pad(entriDate.getDate());
        const month = pad(entriDate.getMonth() + 1);

        return `${day}/${month}/${year2Digit} ${formattedHour}.${formattedMin}.${formattedSec}`;
      }
    }

    return null;
  }, []);

  const processRowData = useCallback((item, index = 0) => {
    let rawEntriValue = item['A'] || item['Tgl Entri'] || item['tglEntri'] || item['Tanggal Entri'] || item['Tgl'] || item['Tanggal'] || item.rawDate;
    
    let entriDate = parseExcelDate(rawEntriValue);

    if (useCustomUploadDate && customUploadDate) {
      const [year, month, day] = customUploadDate.split('-');
      entriDate = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        entriDate.getHours(),
        entriDate.getMinutes(),
        entriDate.getSeconds()
      );
    }

    const supplier = parseSupplierFromRow(item);
    const jawabanProvider = (item['N'] || item.jawabanProvider || item['Jawaban Provider'] || item.message || item.Message || "").toString();
    
    let finalTglStatus = "";
    
    if (supplier === "DIGIFLAZZ") {
      const statusDate = new Date(entriDate.getTime() + 10000);
      finalTglStatus = formatOtomaxDate(statusDate);
    } else if (supplier === "OASIS") {
      const parsedOasisDate = parseOasisTglStatus(jawabanProvider, entriDate);
      if (parsedOasisDate) {
        finalTglStatus = parsedOasisDate;
      } else {
        const timeMatch = jawabanProvider.match(/(\d{1,2}[:\.]\d{2}[:\.]\d{2})/);
        if (timeMatch) {
          const parts = timeMatch[0].split(/[:\.]/);
          let h = parts[0];
          if (h.length > 2) h = h.slice(-1);
          const formattedTime = `${h.padStart(2, '0')}:${parts[1]}:${parts[2]}`;
          finalTglStatus = formatOtomaxDate(entriDate, formattedTime);
        } else {
          const statusDate = new Date(entriDate.getTime() + 10000);
          finalTglStatus = formatOtomaxDate(statusDate);
        }
      }
    } else {
      const timeMatch = jawabanProvider.match(/(\d{2}[:\.]\d{2}[:\.]\d{2})/);
      if (timeMatch) {
        finalTglStatus = formatOtomaxDate(entriDate, timeMatch[0]);
      } else {
        const statusDate = new Date(entriDate.getTime() + 10000);
        finalTglStatus = formatOtomaxDate(statusDate);
      }
    }

    const finalStatus = parseStatusFromRow(item);
    const produk = (item['C'] || item.produk || item['Produk'] || item['Kode Produk'] || item['Nama Produk'] || "").toString().toUpperCase();
    const tujuan = (item['E'] || item.tujuan || item['Tujuan'] || item['No Tujuan'] || "").toString();
    const sn = (item['O'] || item.sn || item['SN'] || "").toString();
    const hargaJual = item['L'] !== undefined ? item['L'] : (item.hargaJual !== undefined ? item.hargaJual : (item['Harga Jual'] || 0));
    const hargaBeli = item['K'] !== undefined ? item['K'] : (item.hargaBeli !== undefined ? item.hargaBeli : (item['Harga Modal'] || 0));
    const laba = item['M'] !== undefined ? item['M'] : (item.laba !== undefined ? item.laba : (item['Profit'] || 0));
    
    const formattedTglEntri = formatOtomaxDate(entriDate);
    // Fingerprint unik transaksi: gabungan beberapa field + supplier agar lebih tahan duplikasi
    const trxKey = `${formattedTglEntri}_${produk}_${tujuan}_${sn}_${laba}_${supplier}`.replace(/\s+/g, '');
    const labaEval = evaluateLaba(laba);

    return {
      id: item.id || `manual_${index}_${Date.now()}`,
      rawDate: entriDate,
      tglEntri: formattedTglEntri,
      tglStatus: finalTglStatus,
      produk,
      tujuan,
      sn,
      status: finalStatus,
      hargaJual,
      hargaBeli,
      laba,
      supplier,
      jawabanProvider,
      trxKey,
      isProblematicLaba: labaEval.isProblem,
      problemReason: labaEval.reason
    };
  }, [useCustomUploadDate, customUploadDate, parseExcelDate, parseSupplierFromRow, formatOtomaxDate, parseOasisTglStatus, parseStatusFromRow]);

  // Olah data Firestore di sini (bukan di dalam listener) supaya perubahan
  // "Ubah tgl" tidak perlu berlangganan ulang ke Firestore.
  const dbData = useMemo(() => {
    const rows = dbRaw.map(raw => processRowData(raw));
    rows.sort((a, b) => b.rawDate - a.rawDate);
    return rows;
  }, [dbRaw, processRowData]);

  // SINKRONISASI MASA BERLAKU DATA UPLOAD MANUAL
  useEffect(() => {
    const today = getTodayString();
    const savedUploadDate = sessionStorage.getItem('speed_excel_upload_date');

    if (savedUploadDate && savedUploadDate !== today) {
      sessionStorage.removeItem('speed_excel_upload_date');
      sessionStorage.removeItem('speed_excel_file_name');
      sessionStorage.removeItem('speed_excel_manual_data');
      setManualData([]);
      setIsManualUpload(false);
      setFileName('');
    } else {
      const storedFileName = sessionStorage.getItem('speed_excel_file_name');
      const storedManualData = sessionStorage.getItem('speed_excel_manual_data');
      if (storedFileName && storedManualData) {
        try {
          const parsed = JSON.parse(storedManualData);
          setFileName(storedFileName);
          setManualData(parsed);
          setIsManualUpload(true);
        } catch (e) {
          console.error("Gagal membaca session data Excel:", e);
        }
      }
    }
  }, [getTodayString, wibDayKey]);

  // PROSES SINKRONISASI 1 HARI TERAKHIR (24 JAM TERAKHIR) DARI FIRESTORE
  useEffect(() => {
    let unsubscribe = () => {};
    setIsLoading(true);
    setErrorMessage('');

    // Hanya data Excel yang diupload dari App.js pada tanggal WIB hari ini yang dipakai
    const todayKey = wibDayKey;

    try {
      // 1. QUERY FILTER DIRECT: Hanya memuat data upload tanggal hari ini (WIB)
      const q = query(
        collection(db, "speed_log_data"),
        where("uploadDay", "==", todayKey)
      );

      unsubscribe = onSnapshot(q, (snapshot) => {
        setDbRaw(snapshot.docs.map(docSnap => ({ ...docSnap.data(), id: docSnap.id })));
        setIsLoading(false);
      }, (error) => {
        console.error("Firebase sync error:", error);
        setErrorMessage("Gagal menyinkronkan data dengan Firebase: " + error.message);
        setIsLoading(false);
      });

      // 2. BACKGROUND CLEANUP AUTOMATION: Otomatis hapus data upload hari-hari sebelumnya
      const cleanupOldData = async () => {
        try {
          const oldDataQuery = query(
            collection(db, "speed_log_data"),
            where("uploadDay", "<", todayKey)
          );
          const oldDocsSnapshot = await getDocs(oldDataQuery);

          if (!oldDocsSnapshot.empty) {
            const BATCH_SIZE = 400;
            const docs = oldDocsSnapshot.docs;
            for (let i = 0; i < docs.length; i += BATCH_SIZE) {
              const batch = writeBatch(db);
              const chunk = docs.slice(i, i + BATCH_SIZE);
              chunk.forEach(docSnap => batch.delete(docSnap.ref));
              await batch.commit();
            }
            console.log(`[Background Cleanup] Berhasil menghapus ${docs.length} data lama (> 1 hari).`);
          }
        } catch (cleanupErr) {
          console.error("Background cleanup error:", cleanupErr);
        }
      };

      cleanupOldData();

    } catch (err) {
      console.error("Firestore Error:", err);
      setErrorMessage("Error koneksi database: " + err.message);
      setIsLoading(false);
    }

    return () => unsubscribe();
  }, [wibDayKey]);

  useEffect(() => {
    if (isManualUpload && manualData.length > 0) {
      setManualData(prev => prev.map((item, idx) => processRowData(item, idx)));
    }
  }, [useCustomUploadDate, customUploadDate, isManualUpload, processRowData]);

  useEffect(() => {
    if (isAutoFit) {
      setColumnWidths({
        tglEntri: 150, tglStatus: 150, produk: 110, tujuan: 150, sn: 240, status: 100,
        hargaJual: 120, hargaBeli: 120, laba: 110, supplier: 130, jawabanProvider: 420
      });
    } else {
      setColumnWidths(defaultWidths);
    }
  }, [isAutoFit, defaultWidths]);

  useEffect(() => {
    let raf = null;
    let pending = null;

    const flush = () => {
      raf = null;
      if (!pending) return;
      const { col, width } = pending;
      pending = null;
      setColumnWidths(prev => (prev[col] === width ? prev : { ...prev, [col]: width }));
    };

    const onMouseMove = (e) => {
      if (!resizingColumn.current) return;
      const clientX = e.touches ? e.touches[0].pageX : e.pageX;
      const diff = clientX - startX.current;
      pending = { col: resizingColumn.current, width: Math.max(20, startWidth.current + diff) };
      if (raf === null) raf = requestAnimationFrame(flush);
    };

    const onMouseUp = () => { 
      resizingColumn.current = null; 
      document.body.style.cursor = 'default'; 
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onMouseMove);
    window.addEventListener('touchend', onMouseUp);
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onMouseMove);
      window.removeEventListener('touchend', onMouseUp);
    };
  }, []);

  const handleResizeStart = useCallback((e, colKey, width) => {
    const clientX = e.touches ? e.touches[0].pageX : e.pageX;
    resizingColumn.current = colKey;
    startX.current = clientX;
    startWidth.current = width;
    document.body.style.cursor = 'col-resize';
  }, []);

  const handleReset = () => {
    setSearchInput('');
    setSearchTerm('');
    setStatusFilter('ALL');
    setDateFilter('');
    setIsAutoFit(false);
    setUseCustomUploadDate(false);
    setCustomUploadDate('');
    setFilterNaOnly(false);
    setColumnWidths(defaultWidths);
  };

  const handleResetDatabase = async () => {
    if (!window.confirm("Apakah Om yakin ingin MENGHAPUS PERMANEN seluruh database Log Speed dari Firebase?")) {
      return;
    }

    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "speed_log_data"));
      const docs = querySnapshot.docs;
      
      const BATCH_SIZE = 400;
      for (let i = 0; i < docs.length; i += BATCH_SIZE) {
        const chunk = docs.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        chunk.forEach(d => {
          batch.delete(d.ref);
        });
        await batch.commit();
      }

      setDbRaw([]);
      clearFile();
      alert("Database Firebase Log Speed berhasil di-reset total!");
    } catch (err) {
      console.error("Gagal melakukan reset database Firebase:", err);
      alert("Gagal reset database: " + err.message);
      setErrorMessage("Reset database gagal: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFile = () => {
    setFileName('');
    setManualData([]);
    setIsManualUpload(false);
    sessionStorage.removeItem('speed_excel_upload_date');
    sessionStorage.removeItem('speed_excel_file_name');
    sessionStorage.removeItem('speed_excel_manual_data');
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsLoading(true);
      setErrorMessage('');
      setFileName(file.name);
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const dataArray = new Uint8Array(e.target.result);
          const workbook = XLSX.read(dataArray, { type: 'array', cellDates: true });
          const rawJson = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { 
            defval: "",
            header: "A" 
          });
          
          const firstCell = String(rawJson[0]?.['A'] || '').toLowerCase();
          const dataRows = (firstCell.includes('tgl') || firstCell.includes('entri') || firstCell.includes('tanggal')) ? rawJson.slice(1) : rawJson;
          
          const processedManualRows = dataRows.map((row, idx) => processRowData(row, idx));
          processedManualRows.sort((a, b) => b.rawDate - a.rawDate);

          setManualData(processedManualRows);
          setIsManualUpload(true);

          const todayStr = getTodayString();
          sessionStorage.setItem('speed_excel_upload_date', todayStr);
          sessionStorage.setItem('speed_excel_file_name', file.name);
          sessionStorage.setItem('speed_excel_manual_data', JSON.stringify(processedManualRows));
        } catch (err) { 
          console.error("Error processing excel:", err); 
          setErrorMessage("Gagal memproses file Excel: " + err.message);
        } finally {
          setIsLoading(false);
        }
      };
      reader.onerror = () => {
        setErrorMessage("Gagal membaca file dari disk.");
        setIsLoading(false);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const activeData = isManualUpload ? manualData : dbData;

  const filteredData = useMemo(() => {
    // Tanpa filter aktif: pakai data apa adanya (tanpa menyalin array)
    if (!searchTerm && statusFilter === 'ALL' && !dateFilter && !filterNaOnly) return activeData;

    const s = searchTerm.toLowerCase();
    const statusLower = statusFilter.toLowerCase();
    return activeData.filter(item => {
      const matchesSearch = !s ||
                            item.tujuan.toLowerCase().includes(s) || 
                            item.produk.toLowerCase().includes(s) || 
                            item.sn.toLowerCase().includes(s);
      const matchesStatus = statusFilter === 'ALL' || item.status.toLowerCase() === statusLower;
      let matchesDate = true;
      if (dateFilter) {
        const itemDateStr = item.rawDate instanceof Date && !isNaN(item.rawDate) 
          ? item.rawDate.toISOString().split('T')[0] 
          : '';
        matchesDate = itemDateStr === dateFilter;
      }
      const isNA = item.sn.toUpperCase() === 'N/A' || item.sn.toUpperCase() === 'NA';
      const matchesNa = !filterNaOnly || isNA;

      return matchesSearch && matchesStatus && matchesDate && matchesNa;
    });
  }, [activeData, searchTerm, statusFilter, dateFilter, filterNaOnly]);

  // Statistik dihitung sekali jalan (sebelumnya 4 kali loop terpisah)
  const { naRows, minusLabaRows, totalLaba, totalMinusLaba } = useMemo(() => {
    const na = [];
    const minus = [];
    let total = 0;
    let minusTotal = 0;
    for (let i = 0; i < filteredData.length; i++) {
      const item = filteredData[i];
      const snUpper = item.sn.toUpperCase();
      if (snUpper === 'N/A' || snUpper === 'NA') na.push(item);
      const laba = Number(item.laba);
      total += laba || 0;
      if (laba < 0) {
        minus.push(item);
        minusTotal += laba;
      }
    }
    return { naRows: na, minusLabaRows: minus, totalLaba: total, totalMinusLaba: minusTotal };
  }, [filteredData]);

  const copyToClipboard = () => {
    const text = naRows.map(item => 
      `Komplain SN N/A:\nTrx: ${item.produk}\nTujuan: ${item.tujuan}\nTgl: ${item.tglEntri}\nStatus: ${item.status}\nSN: ${item.sn}`
    ).join('\n\n');
    
    navigator.clipboard.writeText(text);
    alert('Format komplain berhasil disalin!');
  };


  // ============================================================
  // PANEL TRANSAKSI BERMASALAH (menggantikan notifikasi Telegram)
  // Kriteria: SN = N/A, atau Harga Jual / Harga Beli / Laba bernilai minus.
  // Ditampilkan langsung di bagian atas halaman, tanpa kirim pesan keluar.
  // ============================================================
  const getProblemReasons = useCallback((item) => {
    const reasons = [];
    const snUpper = String(item.sn || '').trim().toUpperCase();
    if (snUpper === 'N/A' || snUpper === 'NA') reasons.push('SN N/A');
    if (Number(item.laba) < 0) reasons.push('Laba Minus');
    if (Number(item.hargaJual) < 0 || Number(item.hargaBeli) < 0) reasons.push('Harga Minus');
    return reasons;
  }, []);

  const problemRows = useMemo(() => {
    const seen = new Set();
    const result = [];
    activeData.forEach(item => {
      const reasons = getProblemReasons(item);
      if (reasons.length === 0) return;
      const key = item.trxKey || item.id;
      if (seen.has(key)) return;
      seen.add(key);
      result.push({ ...item, reasons });
    });
    return result;
  }, [activeData, getProblemReasons]);

  const [showProblemList, setShowProblemList] = useState(true);
  const [problemCopied, setProblemCopied] = useState(false);

  const copyProblemRows = () => {
    const text = problemRows.map((item, i) =>
      `${i + 1}. ${item.produk || '-'} [${item.reasons.join(' | ')}]\n` +
      `Tgl Entri: ${item.tglEntri || '-'}\n` +
      `Tujuan: ${item.tujuan || '-'}\n` +
      `SN: ${item.sn || '-'}\n` +
      `Status: ${item.status || '-'}\n` +
      `Supplier: ${item.supplier || '-'}\n` +
      `Harga Jual: Rp ${formatRupiah(item.hargaJual)} | Harga Beli: Rp ${formatRupiah(item.hargaBeli)} | Laba: Rp ${formatRupiah(item.laba)}`
    ).join('\n\n');

    navigator.clipboard.writeText(text);
    setProblemCopied(true);
    setTimeout(() => setProblemCopied(false), 2000);
  };

  // ============================================================
  // GESER KANAN-KIRI (untuk kebutuhan screenshot / SS Speed)
  // ============================================================
  const tableScrollRef = useRef(null);
  const [scrollState, setScrollState] = useState({ left: false, right: false });

  const updateScrollState = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 4;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setScrollState(prev => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [updateScrollState, columnWidths, filteredData.length, isLoading]);

  const scrollTable = (direction) => {
    const el = tableScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.round(el.clientWidth * 0.7), behavior: 'smooth' });
  };

  const scrollTableTo = (position) => {
    const el = tableScrollRef.current;
    if (!el) return;
    el.scrollTo({ left: position === 'start' ? 0 : el.scrollWidth, behavior: 'smooth' });
  };

  const IDX_WIDTH = 44;
  const totalTableWidth = useMemo(
    () => IDX_WIDTH + Object.values(columnWidths).reduce((sum, w) => sum + w, 0),
    [columnWidths]
  );

  const hasIssues = naRows.length > 0 || minusLabaRows.length > 0;

  return (
    <div className="w-full max-w-full overflow-hidden p-1 md:p-0 font-sans text-slate-800">

      {/* HEADER HALAMAN */}
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
            <Activity size={19} />
          </div>
          <div>
            <h2 className="text-base font-bold leading-tight text-slate-900">Log Speed Transaksi</h2>
            <p className="text-xs text-slate-500">Pantau transaksi hari ini ({wibDayKey}, WIB)</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isManualUpload && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
              <FileSpreadsheet size={12} />
              Mode manual, berlaku 1 hari
            </span>
          )}
          {hasIssues ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-200">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
              </span>
              Ada kelainan transaksi
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
              <CheckCircle2 size={12} />
              Semua normal
            </span>
          )}
        </div>
      </div>

      {/* RINGKASAN + PANEL PERINGATAN */}
      <div className="mb-3 flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-5">
          <StatCard icon={Activity} label="Total transaksi" value={filteredData.length} tone="indigo" />
          <StatCard icon={Wallet} label="Total laba" value={`Rp ${formatRupiah(totalLaba)}`} tone="emerald" valueClass="text-emerald-700" />
          <StatCard icon={HelpCircle} label="Transaksi SN N/A" value={naRows.length} tone="amber" valueClass={naRows.length > 0 ? 'text-amber-700' : 'text-slate-900'} />
          <StatCard icon={TrendingDown} label="Transaksi laba minus" value={minusLabaRows.length} tone="rose" valueClass={minusLabaRows.length > 0 ? 'text-rose-700' : 'text-slate-900'} />
          <div className="col-span-2 lg:col-span-1">
            <StatCard icon={AlertTriangle} label="Total nominal minus" value={`Rp ${formatRupiah(totalMinusLaba)}`} tone="rose" valueClass={totalMinusLaba < 0 ? 'text-rose-700' : 'text-slate-900'} />
          </div>
        </div>

        {/* PANEL PERINGATAN TRANSAKSI BERMASALAH */}
        {!isLoading && problemRows.length > 0 && (
          <div className="bg-white rounded-xl border border-amber-200 border-l-4 border-l-red-500 shadow-sm overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3 py-2.5 bg-gradient-to-r from-red-50 via-amber-50/60 to-white">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="p-1.5 bg-red-100 rounded-lg text-red-600 shrink-0">
                  <AlertTriangle size={15} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-xs text-red-900 uppercase tracking-wider">
                    Peringatan Transaksi Bermasalah
                  </h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Ditemukan <span className="font-bold text-red-700">{problemRows.length}</span> transaksi dengan SN N/A atau nilai minus
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={copyProblemRows}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[10px] font-semibold text-slate-600 transition-colors"
                >
                  {problemCopied ? <CheckCircle2 size={11} className="text-emerald-600" /> : <Copy size={11} />}
                  {problemCopied ? 'Tersalin' : 'Salin Daftar'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowProblemList(prev => !prev)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[10px] font-semibold text-slate-600 transition-colors"
                  aria-expanded={showProblemList}
                >
                  {showProblemList ? 'Sembunyikan' : 'Tampilkan'}
                  <ChevronDown size={11} className={`transition-transform duration-200 ${showProblemList ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>

            {showProblemList && (
              <div className="max-h-60 overflow-auto border-t border-slate-100">
                <table className="w-full min-w-[760px] text-[11px] font-sans border-collapse">
                  <thead className="sticky top-0 z-10 bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="px-2 py-1.5 text-center font-semibold w-8">#</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Tgl. Entri</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Produk</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Tujuan</th>
                      <th className="px-2 py-1.5 text-left font-semibold">SN</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Supplier</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Harga Jual</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Harga Beli</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Laba</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Masalah</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {problemRows.map((item, i) => (
                      <tr key={item.trxKey || item.id || i} className="hover:bg-amber-50/40 transition-colors">
                        <td className="px-2 py-1.5 text-center text-slate-400">{i + 1}</td>
                        <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{item.tglEntri}</td>
                        <td className="px-2 py-1.5 font-semibold text-slate-800 whitespace-nowrap">{item.produk || '-'}</td>
                        <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{item.tujuan || '-'}</td>
                        <td className="px-2 py-1.5 font-mono text-slate-600 whitespace-nowrap">{item.sn || '-'}</td>
                        <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{item.supplier || '-'}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700 whitespace-nowrap">{formatRupiah(item.hargaJual)}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700 whitespace-nowrap">{formatRupiah(item.hargaBeli)}</td>
                        <td className={`px-2 py-1.5 text-right whitespace-nowrap ${Number(item.laba) < 0 ? 'font-bold text-red-600' : 'text-slate-700'}`}>
                          {formatRupiah(item.laba)}
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex flex-wrap gap-1">
                            {item.reasons.map(reason => (
                              <span
                                key={reason}
                                className={`px-1.5 py-0.5 rounded-md text-[9px] font-bold border whitespace-nowrap ${
                                  reason === 'SN N/A'
                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                    : 'bg-red-50 text-red-700 border-red-200'
                                }`}
                              >
                                {reason}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* TABEL LOG SPEED */}
      <section className="relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md">

        {/* Toolbar filter */}
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/80 p-2.5 select-none">
          <label className="flex h-9 min-w-[140px] flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 md:flex-none md:w-44">
            <Calendar size={14} className="shrink-0 text-slate-400" />
            <input type="date" className="w-full bg-transparent text-xs outline-none" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
          </label>

          <label className="flex h-9 min-w-[140px] flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 md:max-w-xs">
            <SearchIcon size={14} className="shrink-0 text-slate-400" />
            <input type="text" className="w-full bg-transparent text-xs outline-none placeholder:text-slate-400" placeholder="Cari tujuan, produk, atau SN" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          </label>

          <select
            className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">Semua status</option>
            <option value="Sukses">Sukses</option>
            <option value="Gagal">Gagal</option>
          </select>

          {!fileName ? (
            <label
              className="flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
              title="Upload manual sementara (berlaku 1 hari)"
            >
              <FileSpreadsheet size={14} className="text-emerald-600" />
              <span>Upload manual</span>
              <input ref={fileInputRef} type="file" className="hidden" accept=".xlsx, .xls, .csv" onChange={handleFileUpload} />
            </label>
          ) : (
            <div className="flex h-9 items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-xs text-emerald-800">
              <FileSpreadsheet size={14} className="shrink-0 text-emerald-600" />
              <span className="max-w-[110px] sl-clip font-medium" title={fileName}>{fileName}</span>
              <button onClick={clearFile} className="rounded p-0.5 text-emerald-700 transition-colors hover:bg-emerald-100 hover:text-rose-600" title="Tutup file manual">
                <Trash2 size={13} />
              </button>
            </div>
          )}

          <div className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5">
            <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-700">
              <input
                type="checkbox"
                checked={useCustomUploadDate}
                onChange={(e) => setUseCustomUploadDate(e.target.checked)}
                className="h-3.5 w-3.5 accent-indigo-600"
              />
              Ubah tgl
            </label>
            {useCustomUploadDate && (
              <input
                type="date"
                className="h-6 rounded border border-slate-200 bg-white px-1.5 text-xs outline-none focus:border-indigo-400"
                value={customUploadDate}
                onChange={(e) => setCustomUploadDate(e.target.value)}
              />
            )}
          </div>

          <button
            onClick={() => setFilterNaOnly(prev => !prev)}
            className={`flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors ${
              filterNaOnly
                ? 'border-amber-500 bg-amber-500 text-white'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <AlertTriangle size={14} className={filterNaOnly ? 'text-white' : 'text-amber-500'} />
            Cek SN
          </button>
        </div>

        {/* Area tabel (geser kanan-kiri) */}
        <div className="relative bg-white" style={{ minHeight: 260 }}>
          {isLoading && (
            <div className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-2 bg-white/80 backdrop-blur-[1px]">
              <Loader2 size={28} className="animate-spin text-indigo-600" />
              <span className="text-xs font-medium text-slate-600">Memuat data transaksi hari ini...</span>
            </div>
          )}

          <div
            ref={tableScrollRef}
            onScroll={updateScrollState}
            className="sl-scroll w-full overflow-auto"
            style={{ maxHeight: TABLE_MAX_HEIGHT }}
          >
            <table
              className="border-separate"
              style={{ tableLayout: 'fixed', borderSpacing: 0, width: totalTableWidth, minWidth: '100%' }}
            >
              <thead>
                <tr>
                  <th className="sl-th sl-th-idx" style={{ width: IDX_WIDTH }}>#</th>
                  <HeaderCell title="Tgl. entri" colKey="tglEntri" width={columnWidths.tglEntri} onResizeStart={handleResizeStart} />
                  <HeaderCell title="Tgl. status" colKey="tglStatus" width={columnWidths.tglStatus} onResizeStart={handleResizeStart} />
                  <HeaderCell title="Produk" colKey="produk" width={columnWidths.produk} onResizeStart={handleResizeStart} />
                  <HeaderCell title="Tujuan" colKey="tujuan" width={columnWidths.tujuan} onResizeStart={handleResizeStart} />
                  <HeaderCell title="SN" colKey="sn" width={columnWidths.sn} onResizeStart={handleResizeStart} />
                  <HeaderCell title="Status" colKey="status" width={columnWidths.status} onResizeStart={handleResizeStart} />
                  <HeaderCell title="Harga jual" colKey="hargaJual" width={columnWidths.hargaJual} align="right" onResizeStart={handleResizeStart} />
                  <HeaderCell title="Harga beli" colKey="hargaBeli" width={columnWidths.hargaBeli} align="right" onResizeStart={handleResizeStart} />
                  <HeaderCell title="Laba" colKey="laba" width={columnWidths.laba} align="right" onResizeStart={handleResizeStart} />
                  <HeaderCell title="Supplier" colKey="supplier" width={columnWidths.supplier} onResizeStart={handleResizeStart} />
                  <HeaderCell title="Jawaban provider" colKey="jawabanProvider" width={columnWidths.jawabanProvider} onResizeStart={handleResizeStart} />
                </tr>
              </thead>
              <tbody>
                {errorMessage && !isLoading && (
                  <tr>
                    <td colSpan={12} className="bg-rose-50/60 py-14 text-center">
                      <div className="flex flex-col items-center justify-center gap-1">
                        <AlertTriangle size={26} className="mb-1 text-rose-500" />
                        <span className="text-sm font-semibold text-rose-700">{errorMessage}</span>
                        <span className="text-xs text-slate-500">Periksa koneksi internet, lalu muat ulang halaman.</span>
                      </div>
                    </td>
                  </tr>
                )}

                {!isLoading && !errorMessage && filteredData.length === 0 && (
                  <tr>
                    <td colSpan={12} className="py-14 text-center">
                      <div className="flex flex-col items-center justify-center gap-1">
                        <Inbox size={28} className="mb-1 text-slate-300" />
                        <span className="text-sm font-semibold text-slate-600">Belum ada data transaksi hari ini</span>
                        <span className="text-xs text-slate-400">Unggah file terbaru lewat App.js atau tombol Upload manual.</span>
                      </div>
                    </td>
                  </tr>
                )}

                {!isLoading && !errorMessage && filteredData.length > 0 && (
                  <VirtualRows rows={filteredData} scrollRef={tableScrollRef} />
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bar komplain SN N/A */}
        {!isLoading && naRows.length > 0 && (
          <div className="flex shrink-0 flex-col gap-2 border-t border-amber-200 bg-amber-50 px-3 py-2 select-none sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-xs font-semibold text-amber-800">
              <AlertTriangle size={15} className="shrink-0 text-amber-600" />
              <span>Ditemukan {naRows.length} transaksi dengan SN N/A</span>
            </div>
            <button
              onClick={copyToClipboard}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100"
            >
              <Copy size={12} />
              Salin komplain
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50/80 px-3 py-2 text-xs select-none">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1" title="Geser tabel ke kiri atau kanan">
              <button
                type="button"
                onClick={() => scrollTableTo('start')}
                disabled={!scrollState.left}
                className="sl-nav-btn"
                aria-label="Ke kolom paling kiri"
              >
                <ChevronsLeft size={15} />
              </button>
              <button
                type="button"
                onClick={() => scrollTable(-1)}
                disabled={!scrollState.left}
                className="sl-nav-btn"
                aria-label="Geser ke kiri"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                type="button"
                onClick={() => scrollTable(1)}
                disabled={!scrollState.right}
                className="sl-nav-btn"
                aria-label="Geser ke kanan"
              >
                <ChevronRight size={15} />
              </button>
              <button
                type="button"
                onClick={() => scrollTableTo('end')}
                disabled={!scrollState.right}
                className="sl-nav-btn"
                aria-label="Ke kolom paling kanan"
              >
                <ChevronsRight size={15} />
              </button>
            </div>

            <label className="flex cursor-pointer items-center gap-1.5 text-slate-600">
              <input
                type="checkbox"
                checked={isAutoFit}
                onChange={(e) => setIsAutoFit(e.target.checked)}
                className="h-3.5 w-3.5 accent-indigo-600"
              />
              Lebarkan kolom
            </label>

            <span className="hidden text-slate-400 md:inline">
              Menampilkan {filteredData.length} dari {activeData.length} transaksi
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              className="inline-flex h-8 items-center rounded-lg border border-slate-200 bg-white px-3 font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              Reset filter
            </button>
            <button
              onClick={handleResetDatabase}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 font-semibold text-rose-600 transition-colors hover:bg-rose-50"
              title="Hapus seluruh database permanen di Firebase"
            >
              <RotateCcw size={12} />
              Reset database
            </button>
          </div>
        </div>

        {/* Stylesheet komponen */}
        <style>{`
          .sl-scroll {
            scrollbar-width: thin;
            scrollbar-color: #cbd5e1 #f8fafc;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior-x: contain;
            touch-action: pan-x pan-y;
          }
          .sl-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
          .sl-scroll::-webkit-scrollbar-track { background: #f8fafc; }
          .sl-scroll::-webkit-scrollbar-thumb {
            background: #cbd5e1; border-radius: 8px; border: 2px solid #f8fafc;
          }
          .sl-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

          .sl-clip { overflow: hidden; text-overflow: clip; white-space: nowrap; }

          /* Header putih polos */
          .sl-th {
            position: sticky; top: 0; z-index: 20;
            height: ${TABLE_HEADER_HEIGHT}px;
            background: #ffffff;
            border-bottom: 1px solid #cbd5e1;
            border-right: 1px solid #eaeef3;
            font-size: 12px; font-weight: 500; color: #475569;
            text-align: left; overflow: hidden; box-sizing: border-box;
          }
          .sl-th-idx { left: 0; z-index: 30; text-align: center; color: #94a3b8; }

          .sl-resizer {
            position: absolute; right: 0; top: 0; bottom: 0; width: 6px;
            cursor: col-resize; z-index: 10;
          }
          .sl-resizer:hover { background: rgba(99, 102, 241, 0.35); }

          .sl-row { transition: background-color 0.12s; }
          .sl-td {
            height: ${TABLE_ROW_HEIGHT}px; padding: 0 12px;
            border-bottom: 1px solid #d5dce6;
            border-right: 1px solid #eaeef3;
            font-size: 12px; color: #334155;
            white-space: nowrap; overflow: hidden; text-overflow: clip;
            vertical-align: middle; user-select: text;
          }

          /* Kolom nomor: bar warna penanda status baris */
          .sl-td-idx {
            position: sticky; left: 0; z-index: 10;
            padding: 0; text-align: center;
            font-size: 11px; color: #94a3b8;
            background: #ffffff;
            border-left: 4px solid #e2e8f0;
            border-right: 1px solid #d5dce6;
          }


          .sl-row-alt { background: #f8fafc; }
          .sl-row-alt .sl-td-idx { background: #f8fafc; }

          .sl-row-fail { background: #fff5f6; }
          .sl-row-fail .sl-td { color: #9f1239; }
          .sl-row-fail .sl-td-idx { background: #fff5f6; border-left-color: #f43f5e; color: #be123c; }

          .sl-row-na { background: #fffbeb; }
          .sl-row-na .sl-td-idx { background: #fffbeb; border-left-color: #f59e0b; color: #b45309; }

          .sl-row-minus { background: #fef7f7; }
          .sl-row-minus .sl-td-idx { background: #fef7f7; border-left-color: #ef4444; color: #b91c1c; }

          @media (hover: hover) {
            .sl-row:hover { background: #eef2ff; }
            .sl-row:hover .sl-td-idx { background: #eef2ff; color: #4338ca; }
          }

          .sl-nav-btn {
            display: inline-flex; align-items: center; justify-content: center;
            width: 32px; height: 32px;
            border-radius: 8px; border: 1px solid #e2e8f0;
            background: #fff; color: #475569; cursor: pointer;
            transition: background-color 0.12s, color 0.12s;
          }
          .sl-nav-btn:hover:not(:disabled) { background: #eef2ff; color: #4338ca; border-color: #c7d2fe; }
          .sl-nav-btn:disabled { opacity: 0.4; cursor: default; }
          .sl-nav-btn:focus-visible,
          .sl-resizer:focus-visible { outline: 2px solid #6366f1; outline-offset: 1px; }
        `}</style>
      </section>
    </div>
  );
};

export default LogSpeedPanel;